import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { UserEditDTO } from '../../../services/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

import Card from '../../../components/common/Card';
import UserForm from '../../../components/forms/UserForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useUserApi } from '../../../hooks/useUserApi';
import { getUserId } from '../../../utils/tokenUtils';

const ProfileEdit: React.FC = () => {
  const [user, setUser] = useState<UserEditDTO>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getById, uploadProfilePicture, edit } = useUserApi();

  const userId = getUserId() || ''

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Profile', href: '/admin/profile' },
    { label: 'Edit', href: '#' },
  ];

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      try {
        if (userId) {
          const response = await getById(userId);
          if (response && response.success && response.data) {
            setUser(response.data as UserEditDTO);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handleSubmit = async (data: UserEditDTO): Promise<void> => {
    setIsSubmitting(true);
    try {
      if (profilePicture) {
        // Call the method to upload the logo image
        const uploadResponse = await uploadProfilePicture(
          profilePicture.name,
          profilePicture,
        );

        if (uploadResponse && uploadResponse.success) {
          data.profilePicture = uploadResponse.data;
        }
      }
      if (userId) {
        const response = await edit(userId, data);
        if (response && response.success && response.data) {
          // Invalidate profile picture cache to force refresh
          queryClient.invalidateQueries({ queryKey: ['profilePictureSas', userId] });
          navigate('/admin/profile');
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/profile"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      {isLoading ? (
        <p>Loading...</p>
      ) : (
          <UserForm
            initialValues={user!}
            isSubmitting={isSubmitting}
            onSave={handleSubmit}
            setProfilePicture={setProfilePicture}
            isCreating={false}
            showPermissions={false}
          />
      )}
    </AdminLayout>
  );
};

export default ProfileEdit;
